import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

const TOTAL_STEPS = 5;

export default function HelpRequest() {
  const { t } = useI18n();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    problem_type: '', priority: '', title: '', description: '', location: '',
    support_needed: [], contact_email: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleSupport = (k) => setData((d) => ({
    ...d,
    support_needed: d.support_needed.includes(k) ? d.support_needed.filter((x) => x !== k) : [...d.support_needed, k],
  }));

  const canNext = () => {
    if (step === 1) return !!data.problem_type;
    if (step === 2) return !!data.priority;
    if (step === 3) return data.title.trim().length >= 3 && data.description.trim().length >= 10;
    if (step === 4) return data.support_needed.length > 0;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...data };
      if (!payload.contact_email) delete payload.contact_email;
      if (!payload.location) delete payload.location;
      await api.post('/help-requests', payload);
      setSuccess(true);
    } catch (e) {
      toast.error(t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setData({ problem_type: '', priority: '', title: '', description: '', location: '', support_needed: [], contact_email: '' });
    setStep(1);
    setSuccess(false);
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="help-success">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 mb-6">
            <CheckCircle2 size={32} strokeWidth={1.75} />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight">{t.help.success_title}</h1>
          <p className="mt-4 text-muted-foreground text-lg leading-relaxed">{t.help.success_body}</p>
          <button onClick={reset} data-testid="help-new-request" className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90">
            {t.help.new_request}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Tikkun</p>
      <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-tight">{t.help.title}</h1>
      <p className="mt-3 text-muted-foreground text-lg">{t.help.subtitle}</p>

      {/* Progress */}
      <div className="mt-10 mb-12">
        <div className="flex items-center justify-between text-xs uppercase tracking-widest text-muted-foreground mb-2">
          <span>{t.help.step} {step} {t.help.of} {TOTAL_STEPS}</span>
          <span>{Math.round((step / TOTAL_STEPS) * 100)}%</span>
        </div>
        <div className="h-1 rounded-full bg-secondary overflow-hidden">
          <motion.div
            initial={false}
            animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="h-full bg-brand-deep dark:bg-brand-gold-dark"
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.25 }}
          className="min-h-[280px]"
        >
          {step === 1 && (
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mb-6">{t.help.s1_title}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {t.help.s1_types.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => update('problem_type', opt.key)}
                    data-testid={`problem-type-${opt.key}`}
                    className={`text-left px-5 py-4 rounded-2xl border transition-all ${data.problem_type === opt.key ? 'border-primary bg-secondary' : 'border-border hover:border-foreground/30'}`}
                  >
                    <span className="font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mb-6">{t.help.s2_title}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {t.help.priorities.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => update('priority', p.key)}
                    data-testid={`priority-${p.key}`}
                    className={`text-left px-5 py-4 rounded-2xl border transition-all ${data.priority === p.key ? 'border-primary bg-secondary' : 'border-border hover:border-foreground/30'}`}
                  >
                    <div className="font-medium">{p.label}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{p.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight">{t.help.s3_title}</h2>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.help.s3_title_label}</label>
                <input
                  type="text"
                  value={data.title}
                  onChange={(e) => update('title', e.target.value)}
                  data-testid="help-title-input"
                  className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.help.s3_desc_label}</label>
                <textarea
                  value={data.description}
                  onChange={(e) => update('description', e.target.value)}
                  placeholder={t.help.s3_desc_ph}
                  data-testid="help-description-input"
                  rows={6}
                  className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.help.s3_loc_label}</label>
                <input
                  type="text"
                  value={data.location}
                  onChange={(e) => update('location', e.target.value)}
                  placeholder={t.help.s3_loc_ph}
                  data-testid="help-location-input"
                  className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mb-6">{t.help.s4_title}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {t.help.s4_options.map((opt) => {
                  const active = data.support_needed.includes(opt.key);
                  return (
                    <button
                      key={opt.key}
                      onClick={() => toggleSupport(opt.key)}
                      data-testid={`support-${opt.key}`}
                      className={`text-left px-5 py-4 rounded-2xl border transition-all flex items-center justify-between ${active ? 'border-primary bg-secondary' : 'border-border hover:border-foreground/30'}`}
                    >
                      <span className="font-medium">{opt.label}</span>
                      {active && <Check size={18} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight">{t.help.s5_title}</h2>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.help.s5_email_label}</label>
                <input
                  type="email"
                  value={data.contact_email}
                  onChange={(e) => update('contact_email', e.target.value)}
                  data-testid="help-email-input"
                  className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                />
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{t.help.s5_note}</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Nav */}
      <div className="mt-12 flex items-center justify-between">
        <button
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
          data-testid="help-back-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:pointer-events-none transition-colors"
        >
          <ArrowLeft size={16} /> {t.help.back}
        </button>
        {step < TOTAL_STEPS ? (
          <button
            onClick={() => setStep((s) => s + 1)}
            disabled={!canNext()}
            data-testid="help-next-btn"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-40 disabled:pointer-events-none transition-opacity"
          >
            {t.help.next} <ArrowRight size={16} />
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="help-submit-btn"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
          >
            {submitting ? t.common.loading : t.help.submit} <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
